import { ChevronDown, ChevronUp, Shield, Sliders, WalletCards } from "lucide-react";
import { useId, useState } from "react";

import { formatCompactCurrency } from "../../../../lib/dashboard";
import {
  useProfileEdit,
  type ProfileEditPatch,
  type UseProfileEditResult,
  type UseProfileEditOptions,
} from "../../../../lib/profile/use-profile-edit";
import { cn } from "../../../../lib/utils";
import { Input } from "../../../ui/input";

export interface ProfileGoalRowProps {
  // Current monthly_budget in dollars. null when the user hasn't set one.
  currentValue: number | null;
  // When omitted (e.g., the bootstrap loader didn't surface the current
  // snapshot id), the row still opens as a local-only editor. Persisted
  // editing requires a snapshot id at the type level because the backend
  // rejects empty base_snapshot_id with 422.
  opts?: UseProfileEditOptions;
}

// ProfileGoalRow is the single-fact "Target PITI" row backed by
// PATCH /api/profile (monthly_budget). Other goal rows go through
// useTargetEdit; this one is its own component because monthly_budget
// lives on the consumer profile, not the readiness target.
//
// TODO(SILL-187 follow-up): accept idPrefix prop if ProfileView is ever
// embedded twice on a page.
export function ProfileGoalRow({ currentValue, opts }: ProfileGoalRowProps) {
  if (!opts) {
    return (
      <ProfileGoalRowContent currentValue={currentValue} editProfile={null} />
    );
  }
  return <EditableProfileGoalRow currentValue={currentValue} opts={opts} />;
}

function EditableProfileGoalRow({
  currentValue,
  opts,
}: {
  currentValue: number | null;
  opts: UseProfileEditOptions;
}) {
  const editProfile = useProfileEdit(opts);
  return (
    <ProfileGoalRowContent
      currentValue={currentValue}
      editProfile={editProfile}
    />
  );
}

function ProfileGoalRowContent({
  currentValue,
  editProfile,
}: {
  currentValue: number | null;
  editProfile: UseProfileEditResult | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const sourceDraft = currentValue == null ? "" : String(currentValue);
  const [draftState, setDraftState] = useState({
    base: sourceDraft,
    value: sourceDraft,
  });
  const draft =
    draftState.base === sourceDraft ? draftState.value : sourceDraft;
  const setDraft = (value: string) => {
    setDraftState({ base: sourceDraft, value });
  };
  const editorId = useId();
  const error = editProfile?.error
    ? "Couldn't save — please try again."
    : undefined;
  const isPending = editProfile?.isPending ?? false;

  // Uncontrolled editor — see TargetGoalRow for rationale. We read the
  // input's value at blur time directly from the DOM.
  const handleCommit = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value;
    if (raw === draft) {
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      // Invalid input — gate locally without a network round-trip.
      return;
    }
    const prior = draft;
    setDraft(raw);
    if (editProfile) {
      editProfile.mutate({ monthly_budget: parsed }).catch(() => {
        setDraft(prior);
        // Surfaced via editProfile.error.
      });
    }
  };

  const displayValue = draft === "" ? null : Number(draft);
  const display =
    displayValue && displayValue > 0
      ? `${formatCompactCurrency(displayValue)}/mo`
      : "Not set";

  return (
    <article
      id="profile-row-target_piti"
      data-state={expanded ? "open" : "closed"}
      className={cn(
        "overflow-hidden p-0 transition-colors hover:bg-accent/35",
        expanded && "bg-background/60",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[4.75rem] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
        aria-label="Target PITI"
        aria-expanded={expanded}
        data-slot="interactive-control"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
            <WalletCards
              aria-hidden="true"
              className="size-4"
              strokeWidth={2}
            />
          </span>
          <span className="block min-w-0 truncate text-base font-semibold text-foreground">
            Target PITI
          </span>
        </span>
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-3">
          <span className="block min-w-0 truncate text-right text-sm font-semibold text-foreground">
            {display}
          </span>
          {expanded ? (
            <ChevronUp aria-hidden="true" className="h-4 w-4" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-4 w-4" />
          )}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          <div className="max-w-xl">
            <Input
              key={draft}
              type="number"
              min={0}
              className="font-bold tabular-nums"
              defaultValue={draft}
              aria-label="Target PITI"
              disabled={isPending}
              endAdornment="/mo"
              error={error}
              id={editorId}
              inputMode="numeric"
              label="Target PITI"
              onBlur={handleCommit}
              startAdornment="$"
            />
          </div>
        </div>
      )}
    </article>
  );
}

// ── ReserveFloorRow ───────────────────────────────────────────────────────────

export interface ReserveFloorRowProps {
  // Months of reserves the user wants to keep after closing. Default 3.
  currentValue: number;
  opts?: UseProfileEditOptions;
}

export function ReserveFloorRow({ currentValue, opts }: ReserveFloorRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const editProfile = useProfileEdit(
    opts ?? { getToken: async () => null, getBaseSnapshotId: () => "" },
  );
  const editorId = useId();

  const sourceDraft = String(currentValue);
  const display = `${currentValue} mo`;
  const error = editProfile.error
    ? "Couldn't save — please try again."
    : validationError ?? undefined;

  const handleCommit = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value.trim();
    setValidationError(null);
    if (raw === sourceDraft) return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 24) {
      setValidationError("Enter 0–24 months.");
      return;
    }
    if (!opts) return;
    editProfile
      .mutate({ reserve_floor_months: parsed } as ProfileEditPatch)
      .catch(() => {});
  };

  return (
    <article
      id="profile-row-reserve_floor"
      data-state={expanded ? "open" : "closed"}
      className={cn(
        "overflow-hidden p-0 transition-colors hover:bg-accent/35",
        expanded && "bg-background/60",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[4.75rem] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
        aria-label="Reserve floor"
        aria-expanded={expanded}
        data-slot="interactive-control"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
            <Shield aria-hidden="true" className="size-4" strokeWidth={2} />
          </span>
          <span className="block min-w-0 truncate text-base font-semibold text-foreground">
            Reserve floor
          </span>
        </span>
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-3">
          <span className="block min-w-0 truncate text-right text-sm font-semibold text-foreground">
            {display}
          </span>
          {expanded ? (
            <ChevronUp aria-hidden="true" className="h-4 w-4" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-4 w-4" />
          )}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          <div className="max-w-xl">
            <Input
              key={sourceDraft}
              type="number"
              min={0}
              max={24}
              className="font-bold tabular-nums"
              defaultValue={sourceDraft}
              aria-label="Reserve floor"
              disabled={editProfile.isPending}
              endAdornment="months"
              error={error}
              id={editorId}
              inputMode="numeric"
              label="Reserve floor"
              onBlur={handleCommit}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Months of housing payment to keep in savings after closing. Sill defaults to 3.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

// ── ComfortDtiRow ─────────────────────────────────────────────────────────────

export interface ComfortDtiRowProps {
  // User's comfort DTI ceiling as a percentage (e.g. 43). null uses Sill's default.
  currentValue: number | null;
  opts?: UseProfileEditOptions;
}

export function ComfortDtiRow({ currentValue, opts }: ComfortDtiRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const editProfile = useProfileEdit(
    opts ?? { getToken: async () => null, getBaseSnapshotId: () => "" },
  );
  const editorId = useId();

  const sourceDraft = currentValue !== null ? String(currentValue) : "";
  const display =
    currentValue !== null ? `${currentValue}% ceiling` : "Default (43%)";
  const error = editProfile.error
    ? "Couldn't save — please try again."
    : validationError ?? undefined;

  const handleCommit = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value.trim();
    setValidationError(null);
    if (raw === sourceDraft) return;
    const next = raw === "" ? null : Number.parseFloat(raw);
    if (next !== null && (!Number.isFinite(next) || next < 0 || next > 60)) {
      setValidationError("Enter 0–60%.");
      return;
    }
    if (!opts) return;
    editProfile
      .mutate({ comfort_dti_pct: next } as ProfileEditPatch)
      .catch(() => {});
  };

  return (
    <article
      id="profile-row-comfort_dti"
      data-state={expanded ? "open" : "closed"}
      className={cn(
        "overflow-hidden p-0 transition-colors hover:bg-accent/35",
        expanded && "bg-background/60",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[4.75rem] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
        aria-label="Comfort DTI ceiling"
        aria-expanded={expanded}
        data-slot="interactive-control"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
            <Sliders aria-hidden="true" className="size-4" strokeWidth={2} />
          </span>
          <span className="block min-w-0 truncate text-base font-semibold text-foreground">
            Comfort DTI ceiling
          </span>
        </span>
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-3">
          <span className="block min-w-0 truncate text-right text-sm font-semibold text-foreground">
            {display}
          </span>
          {expanded ? (
            <ChevronUp aria-hidden="true" className="h-4 w-4" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-4 w-4" />
          )}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          <div className="max-w-xl">
            <Input
              key={sourceDraft}
              type="number"
              min={0}
              max={60}
              step={0.5}
              className="font-bold tabular-nums"
              defaultValue={sourceDraft}
              aria-label="Comfort DTI ceiling"
              disabled={editProfile.isPending}
              endAdornment="%"
              error={error}
              id={editorId}
              inputMode="decimal"
              label="Comfort DTI ceiling"
              onBlur={handleCommit}
              placeholder="43"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Lower to make Sill stricter; raise to be more flexible. Sill defaults to 43%.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}
